<template>
  <div class="accum" :class="cssType">
    <Banner
      :realTime="state.realTime"
      :activeStartTime="state.activeStartTime"
      :activeEndTime="state.activityEndTime"
      :pageCloseTime="state.pageCloseTime"
      @end="handleEnd"
    />
    <AppLang class="app-lang" type="accum" />
    <div class="part-1-2" :style="{ zIndex: state.partDialogOpen > 0 ? 1000 : 2 }">
      <ValidateUser
        @dialogOpen="handlePartDialogOpen"
      />
      <!-- 月相进度是全服活动进度，放在用户查询区和个人累充奖励区之间。 -->
      <MoonPhase
        v-if="state.activityLoaded"
        :phaseIndex="state.phaseIndex"
        :phaseCode="state.phaseCode"
        :phaseName="state.phaseName"
        :progressPercent="state.phaseProgress"
        :isFullMoon="state.isFullMoon"
        @dialogOpen="handlePartDialogOpen"
      />
    </div>
    <!-- 个人累充奖励沿用 accum 原有档位、领取和记录逻辑。 -->
    <Rewards
      :rewardList="state.rewardList"
      :rechargeDiamond="state.rechargeDiamond"
      :style="{ zIndex: state.dialogOpen > 0 ? 1000 : 1 }"
      @dialogOpen="handleDialogOpen"
      @rewarded="reset"
    />
    <Rule :rechargeDiamond="state.rechargeDiamond" />
    <AppFooter />
    <ActFloat v-if="!hasOverlayDialog" :rechargeDiamond="state.rechargeDiamond" />
    <ActTimeDialog v-model="state.actTimeDialog.visible" />
  </div>
</template>

<script setup>
import { reactive, watch, onMounted, onUnmounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useLog } from '@/composables/useLog'
import { isMobile } from '@/utils'
import { localeToRegionIdMap } from '@/config/const'
import MidAutumnApi from '@/config/midAutumnApi'
import Banner from './components/Banner'
import MoonPhase from './components/MoonPhase'
import Rewards from './components/Rewards'
import Rule from './components/Rule'
import AppLang from '@/components/AppLang'
import AppFooter from '@/components/AppFooter'
import ActFloat from './components/ActFloat'
import ValidateUser from '@/components/ValidateUser/ActAutumn/index.vue'
import ActTimeDialog from './components/Dialog/ActTime'

const store = useStore()
const log = useLog()
const cssType = isMobile ? 'mobile' : 'pc'

const state = reactive({
  partDialogOpen: 0,
  dialogOpen: 0,
  activityLoaded: false,
  status: 'not_started',
  phaseCode: 'new_moon',
  phaseName: '',
  phaseIndex: 0,
  phaseProgress: 0,
  isFullMoon: false,
  isLoggedIn: false,
  isRecharge: false,
  rewardList: [],
  rechargeDiamond: 0,
  serverTime: 0,
  realTime: 0,
  logTime: 0,
  activeStartTime: 0,
  activityEndTime: 0,
  pageCloseTime: 0,
  actTimeDialog: {
    visible: false,
  },
})

let serverClockTimer = 0
let activityInfoRequesting = false
let rewardRequesting = false
let pageEntryTracked = false
let disposed = false

const toTimestamp = (value) => {
  if (typeof value === 'number') return value < 100000000000 ? value * 1000 : value
  if (!value) return 0
  const timestamp = Date.parse(value)
  return Number.isNaN(timestamp) ? 0 : timestamp
}

const applyActivityInfo = (data = {}) => {
  const serverTime = Number(data.serverTime) || Date.now()
  state.serverTime = serverTime
  state.logTime = Date.now()
  state.realTime = serverTime
  state.activeStartTime = toTimestamp(data.activityStartTime)
  state.activityEndTime = toTimestamp(data.activityEndTime)
  state.pageCloseTime = toTimestamp(data.pageCloseTime)
  state.phaseCode = data.phaseCode || 'new_moon'
  state.phaseName = data.phaseName || ''
  state.phaseIndex = Number(data.phaseIndex) || 0
  state.phaseProgress = Math.min(Math.max(Number(data.phaseProgress) || 0, 0), 100)
  state.isFullMoon = Boolean(data.isFullMoon)
  state.status = data.status || 'active'
  state.activityLoaded = true
}

const startServerClock = () => {
  if (serverClockTimer) clearInterval(serverClockTimer)
  serverClockTimer = window.setInterval(() => {
    if (state.serverTime && state.logTime) {
      state.realTime = state.serverTime + Date.now() - state.logTime
    }
  }, 1000)
}

const getActivityInfo = async () => {
  if (activityInfoRequesting) return
  activityInfoRequesting = true
  try {
    const res = await MidAutumnApi.getActivityInfo({ _loading: 0 })
    applyActivityInfo(res)
    startServerClock()
    const moonPhase = Number(res.phaseIndex)
    if (!disposed && !pageEntryTracked && res.phaseIndex != null && res.phaseIndex !== '' && Number.isInteger(moonPhase) && moonPhase >= 0 && moonPhase <= 4) {
      pageEntryTracked = true
      try {
        log.track('moon_page_entry', {
          moon_phase: moonPhase,
        })
      } catch (error) {
        console.error(error)
      }
    }
  } catch (error) {
    log.track('act_mid_autumn_api_error', { v1: String(error) })
  } finally {
    activityInfoRequesting = false
  }
}

const currentUser = () => store.state.cpUserInfo || {}

const getRewardList = async () => {
  // 列表可在活动开始前、关闭后查看；请求进行中时直接跳过，不排队补发。
  if (rewardRequesting) return

  const user = currentUser()
  const userAccountId = user.cpUserId || user.userAccountId || ''
  const accountNum = user.accountNum || ''

  rewardRequesting = true
  try {
    // userAccountId 使用聚合返回的 cpUserId（账号服发奖 UID），accountNum 只用于查询聚合 rewardList。
    // 不再把 accountNum 当作活动用户 ID，避免把 zzy 这类聚合账号写入活动表。
    const res = await MidAutumnApi.getRewardList({
      userAccountId: userAccountId,
      accountNum: accountNum,
      openId: accountNum,
      userNickname: user.cpNickName || user.userNickname || '',
      regionId: localeToRegionIdMap[store.state.locale] || '',
      _loading: 0,
    })
    const list = Array.isArray(res.list) ? res.list : []
    // 中秋后端代理保留聚合 rewardList 原始字段；这里继续沿用 accum 的
    // 客户端状态计算，确保个人累充档位、领取按钮和进度条逻辑不变。
    list.sort((a, b) => Number(a.needPurchaseCount || 0) - Number(b.needPurchaseCount || 0))
    list.forEach(item => {
      item.status = getRewardStatus(item)
    })
    state.rewardList = list
    state.rechargeDiamond = Number(res.totalMergePrdCount) || 0
    state.isLoggedIn = Boolean(res.isLogin)
    state.isRecharge = Boolean(res.isRecharge) || state.rechargeDiamond > 0
  } catch (error) {
    log.track('act_mid_autumn_api_error', { v1: String(error) })
  } finally {
    rewardRequesting = false
  }
}

const getRewardStatus = (item) => {
  // 保持 accum 原有的个人累充档位状态判断。
  if (item.collectTimesLimit === 0) {
    return 'goRecharge'
  }
  if (item.collectedTimes >= item.collectTimesLimit) {
    return 'received'
  }
  if (item.collectedTimes < item.canCollectTimes) {
    return 'canReceive'
  }
  if (item.canCollectTimes < item.collectTimesLimit) {
    return 'notReach'
  }
  return 'notReach'
}

const reset = () => {
  getRewardList()
}

const handleEnd = async () => {
  await getActivityInfo()
  state.actTimeDialog.visible = true
}

const handleDialogOpen = (visible) => {
  state.dialogOpen = visible ? state.dialogOpen + 1 : Math.max(0, state.dialogOpen - 1)
}

const handlePartDialogOpen = (visible) => {
  state.partDialogOpen = visible
    ? state.partDialogOpen + 1
    : Math.max(0, state.partDialogOpen - 1)
}

// 与 accum 一致：弹窗打开时抬高容器层级，并隐藏浮动按钮，避免遮挡
const hasOverlayDialog = computed(() => (
  state.dialogOpen > 0
  || state.partDialogOpen > 0
  || state.actTimeDialog.visible
))

watch(
  () => [currentUser().cpUserId, currentUser().userAccountId, currentUser().accountNum],
  () => reset(),
)

watch(
  () => store.state.locale,
  () => reset(),
)

const handleVisibilityChange = () => {
  if (!document.hidden) {
    getActivityInfo()
    getRewardList()
  }
}

onMounted(async () => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
  await Promise.all([getActivityInfo(), getRewardList()])
})

onUnmounted(() => {
  disposed = true
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (serverClockTimer) clearInterval(serverClockTimer)
})
</script>

<style src="./pc_style.less" lang="less" scoped></style>
<style src="./mobile_style.less" lang="less" scoped></style>
